/**
 * SidebarToggleButton
 * Toggle button for showing/hiding the sidebar
 */
import React from 'react';
import { TouchableOpacity, StyleSheet } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

import { tokens } from '@design';
// PanelLeftClose icon - sidebar visible, click to hide
const PanelLeftCloseIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Panel frame */}
    <Rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="2" />
    {/* Left panel divider */}
    <Path d="M9 3V21" stroke={color} strokeWidth="2" />
    {/* Arrow pointing left (close) */}
    <Path d="M16 12L13 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M16 12L13 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// PanelLeftOpen icon - sidebar hidden, click to show
const PanelLeftOpenIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* Panel frame */}
    <Rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="2" />
    {/* Left panel divider */}
    <Path d="M9 3V21" stroke={color} strokeWidth="2" />
    {/* Arrow pointing right (open) */}
    <Path d="M14 12L17 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M14 12L17 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

interface SidebarToggleButtonProps {
  size?: number;
}

export default function SidebarToggleButton({ size = 20 }: SidebarToggleButtonProps) {
  const { isVisible, toggleSidebar } = useSidebar();
  const { colors } = useTheme();
  const { t } = useLanguage();

  // Theme colors
  const themeColors = {
    buttonBackground: colors.card,
    buttonBorder: colors.cardBorder,
    iconColor: colors.iconDefault,
  };

  return (
    <TouchableOpacity
      style={[styles.button, { backgroundColor: themeColors.buttonBackground, borderColor: themeColors.buttonBorder }]}
      onPress={toggleSidebar}
      accessibilityLabel={isVisible ? t('accessibility.hideSidebar') : t('accessibility.showSidebar')}
      accessibilityHint={isVisible ? t('accessibility.hideSidebarHint') : t('accessibility.showSidebarHint')}
    >
      {isVisible ? (
        <PanelLeftCloseIcon size={size} color={themeColors.iconColor} />
      ) : (
        <PanelLeftOpenIcon size={size} color={themeColors.iconColor} />
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    padding: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.white,
  },
});
